import { memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@aimsos/i18n';
import { X } from 'lucide-react';

import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';

/**
 * Dialog of the bulk import, built like the prototype's: it fills the width up to 900px, has one
 * padded surface (no header or footer dividers), a small close button in the corner, an icon
 * beside the title (or above it, centered) and the actions at the bottom. `ModalLayout` is the
 * shared shell of the platform and keeps its own header, so it is not used here.
 * Mount it only while open.
 *
 * @param {object} props
 * @param {() => void} props.onClose Close button, backdrop and Esc.
 * @param {string} props.titleId
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {import('react').ReactNode} [props.icon] Icon box beside the title (centered: above it).
 * @param {'start'|'center'} [props.align='start']
 * @param {import('react').ReactNode} [props.footer] Buttons, right-aligned (centered when `align="center"`).
 * @param {boolean} [props.isBusy=false] Its action is running: Esc, the backdrop and the X do nothing.
 * @param {string} [props.closeAriaLabel] Defaults to the translated "Close".
 * @param {import('react').ReactNode} props.children
 */
function ImportDialog({
  onClose,
  titleId,
  title,
  subtitle,
  icon = null,
  align = 'start',
  footer = null,
  isBusy = false,
  closeAriaLabel,
  children,
}) {
  const { t } = useTranslation('people-access');
  // While the action runs, no way out may close the dialog under it.
  const dismiss = useCallback(() => {
    if (!isBusy) onClose();
  }, [isBusy, onClose]);
  useDialogKeyboard(true, dismiss, titleId);
  const centered = align === 'center';

  return createPortal(
    <div
      role="presentation"
      className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(event) => event.target === event.currentTarget && dismiss()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex w-full max-w-[900px] flex-col gap-2.5 rounded-2xl border border-edge bg-surface p-6 shadow-2xl"
      >
        <button
          type="button"
          aria-label={closeAriaLabel ?? t('import.close')}
          disabled={isBusy}
          onClick={dismiss}
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-lg text-content-muted transition-colors hover:text-content disabled:pointer-events-none disabled:opacity-40"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        <div className={centered ? 'flex flex-col items-center gap-3 text-center' : 'flex items-center gap-3 pr-8'}>
          {icon}
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-content">
              {title}
            </h2>
            {subtitle ? <p className="mt-0.5 text-xs text-content-muted">{subtitle}</p> : null}
          </div>
        </div>
        {children}
        {footer ? (
          <div className={`mt-2 flex items-center gap-2 ${centered ? 'justify-center' : 'justify-end'}`}>{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export default memo(ImportDialog);
